/**
 * early-access.js
 * ================
 * Summer Sale 2026 — Early Access page controller
 * /pages/summer-sale
 *
 * RESPONSIBILITIES:
 *  1. Bootstrap: read customer data from #SachiEACustomerData
 *  2. Call Supabase resolve-session Edge Function → get session context
 *  3. Cache session in sessionStorage (re-use within tab, avoid repeat calls)
 *  4. Apply discount via /discount/CODE redirect (once per session)
 *  5. Populate tier block (badge, discount %, referral code + copy)
 *  6. Reveal EA prices on all .ea-price elements
 *  7. Populate reorder block if previous_orders exist
 *  8. Initialise GWP progress bar; subscribe to cart updates
 *
 * NAMING PREFIX: early-access_ (matches Supabase function/secret convention)
 *
 * DEPENDENCIES:
 *  - Supabase Edge Function: early-access_resolve-session
 *  - CSS: early-access.css (skeleton shimmer, tier badge, EA price classes)
 *  - FoxThemeEvents pub/sub (available globally via theme's cart.js)
 */

(function () {
  'use strict';

  // ─── CONSTANTS ────────────────────────────────────────────────────────────

  /**
   * Supabase project URL — update with your actual Supabase project URL.
   * Keep the anon key here (it is safe for public use with RLS enabled).
   * NEVER use the service_role key in client-side code.
   */
  const SUPABASE_URL = 'https://yupexdimsfuswwkouewp.supabase.co';
  const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1cGV4ZGltc2Z1c3d3a291ZXdwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjExMzYwNzQsImV4cCI6MjA3NjcxMjA3NH0.O5ljAVpLj__2UWztWB0StyqsAh_96zzzmykK8goBMWc'; // Paste your anon key here — see below

  /** Edge Function endpoint (prefixed early-access_ in Supabase) */
  const RESOLVE_SESSION_ENDPOINT = `${SUPABASE_URL}/functions/v1/early-access_resolve-session`;

  /** sessionStorage key — avoids repeat Supabase calls within the same tab */
  const SESSION_STORAGE_KEY = 'sachi_ea_session';

  /** Page handle — update this if the page URL changes */
  const PAGE_HANDLE = '/pages/summer-sale-early-access';

  /** sessionStorage key — tracks whether discount redirect has already fired */
  const DISCOUNT_APPLIED_KEY = 'sachi_ea_discount_applied';

  /** Campaign window — access denied before this UTC timestamp */
  const CAMPAIGN_OPENS_UTC = '2026-05-30T08:00:00Z';
  const CAMPAIGN_CLOSES_UTC = '2026-06-21T23:59:59Z';

  // ─── BOOT ─────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    const customerDataEl = document.getElementById('SachiEACustomerData');

    // Gate: customer data block must exist (set by early-access-gate.liquid)
    // If not present, the Liquid gate already redirected — nothing to do.
    if (!customerDataEl) return;

    let customer;
    try {
      customer = JSON.parse(customerDataEl.textContent);
    } catch (e) {
      console.error('[EA] Failed to parse customer data:', e);
      return;
    }

    if (!customer || !customer.id) {
      console.warn('[EA] No customer ID found — aborting session load.');
      return;
    }

    initEarlyAccess(customer);
  });

  // ─── INIT ─────────────────────────────────────────────────────────────────

  async function initEarlyAccess(customer) {
    // Show skeleton state on tier block and product prices
    setSkeletonState(true);

    // Check sessionStorage cache first (avoids Supabase call on every page refresh)
    const cached = getCachedSession();
    if (cached) {
      applySession(cached, customer);
      return;
    }

    try {
      const session = await resolveSession(customer);
      setCachedSession(session);
      applySession(session, customer);
    } catch (err) {
      console.error('[EA] Session resolution failed:', err);
      setErrorState('We could not load your early access details. Please refresh to try again.');
    }
  }

  // ─── SESSION RESOLUTION ───────────────────────────────────────────────────

  /**
   * Calls Supabase early-access_resolve-session Edge Function.
   * The Edge Function validates the customer ID against Shopify Admin API,
   * resolves their tier via tags + Smile.io metafield, and returns
   * a full session context object.
   *
   * @param {Object} customer — parsed from #SachiEACustomerData
   * @returns {Promise<Object>} session context
   */
  async function resolveSession(customer) {
    const payload = {
      customer_id: String(customer.id),
      email: customer.email,
      locale: customer.locale,
      currency: customer.currency,
      token: customer.token, // HMAC token generated server-side by Liquid — proves this is a real session
    };

    const res = await fetch(RESOLVE_SESSION_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`[EA] Edge Function ${res.status}: ${errText}`);
    }

    return res.json();
  }

  // ─── APPLY SESSION ────────────────────────────────────────────────────────

  /**
   * Master function — applies all session data to the page.
   * Called whether session came from Supabase or sessionStorage cache.
   *
   * @param {Object} session — session context from Supabase
   * @param {Object} customer — customer identity from DOM
   */
  function applySession(session, customer) {
    setSkeletonState(false);

    // Validate campaign window
    const now = Date.now();
    if (now < new Date(CAMPAIGN_OPENS_UTC).getTime()) {
      setPreLaunchState(customer.firstName);
      return;
    }
    if (now > new Date(CAMPAIGN_CLOSES_UTC).getTime()) {
      setClosedState();
      return;
    }

    // Tier block
    renderTierBlock(session, customer);

    // Product prices
    revealEAPricing(session);

    // Discount application (once per session)
    applyDiscountCode(session);

    // Reorder block
    if (session.previous_orders && session.previous_orders.length > 0) {
      renderReorderBlock(session);
    }

    // GWP progress bar
    initGWPBar(session);
  }

  // ─── TIER BLOCK ───────────────────────────────────────────────────────────

  /**
   * Populates #ea-tier-block with tier badge, discount info, and referral code.
   * The skeleton is replaced with actual content.
   *
   * Session tier values: 'EXPERT' | 'ESSENTIALIST' | 'INSIDER'
   *
   * @param {Object} session
   * @param {Object} customer
   */
  function renderTierBlock(session, customer) {
    const block = document.getElementById('ea-tier-block');
    if (!block) return;

    const tier = session.tier || 'INSIDER';
    const discountPct = session.discount_pct || 20;
    const referralCode = session.referral_code || '';

    const tierLabels = {
      EXPERT: 'Sachi Expert',
      ESSENTIALIST: 'Sachi Essentialist',
      INSIDER: 'Sachi Insider',
    };

    const tierLabel = tierLabels[tier] || 'Sachi Insider';

    // Perks copy per tier
    const perksHtml = buildPerksHtml(session, tier);

    block.innerHTML = `
      <div class="ea-tier-badge ea-tier-badge--${tier.toLowerCase()}" aria-label="Your tier: ${tierLabel}">
        <svg class="ea-tier-badge__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                fill="currentColor"/>
        </svg>
        <span class="ea-tier-badge__name">${tierLabel}</span>
      </div>

      <div class="ea-tier-block__discount">
        <strong>${discountPct}% off</strong> your entire order
        <span class="ea-tier-block__discount-note">— applied automatically at checkout</span>
      </div>

      ${perksHtml}

      ${referralCode ? buildReferralHtml(referralCode, session, tier) : ''}
    `;

    block.classList.add('ea-tier-block--resolved');
    block.removeAttribute('aria-busy');

    // Bind copy-to-clipboard
    const copyBtn = block.querySelector('.ea-copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', handleCopyReferralCode);
    }

    // Screen reader announcement
    setGateStatus(`Your early access is loaded. You have ${tierLabel} access with ${discountPct}% off.`);
  }

  function buildPerksHtml(session, tier) {
    const currency = (session.currency || 'GBP').toUpperCase();
    const isgbp = currency === 'GBP';

    const thresholdVip = isgbp
      ? '£' + (session.ritual_case_threshold_gbp || 90)
      : '$' + (session.ritual_case_threshold_usd || 125);

    const thresholdInsider = isgbp
      ? '£' + (session.ritual_case_threshold_insider_gbp || 120)
      : '$' + (session.ritual_case_threshold_insider_usd || 150);

    const gwpThreshold = (tier === 'EXPERT' || tier === 'ESSENTIALIST')
      ? thresholdVip
      : thresholdInsider;

    const perks = {
      EXPERT: [
        '30% off — no minimum spend',
        `Free Ritual Case when you spend ${gwpThreshold}`,
        'Free shipping on all orders',
        'Priority access to new launches',
      ],
      ESSENTIALIST: [
        '25% off — no minimum spend',
        `Unlock 30% off when you spend ${gwpThreshold}`,
        `Free Ritual Case when you spend ${gwpThreshold}`,
        'Free shipping on all orders',
      ],
      INSIDER: [
        '20% off your entire order',
        `Unlock 25% off when you spend ${gwpThreshold}`,
        'Free shipping on all orders',
      ],
    };

    const perkList = perks[tier] || perks.INSIDER;
    const items = perkList.map(p => `<li>${p}</li>`).join('');
    return `<ul class="ea-tier-block__perks" aria-label="${tier} perks">${items}</ul>`;
  }

  function buildReferralHtml(code, session, tier) {
    const friendPct = session.bring_a_friend_discount_pct || 20;
    const creditPct = session.credit_back_pct || 20;

    return `
      <div class="ea-tier-block__referral">
        <p class="ea-tier-block__referral-label">Your referral code</p>
        <div class="ea-referral-code" role="group" aria-label="Referral code: ${code}">
          <code id="ea-ref-code" class="ea-referral-code__value">${code}</code>
          <button
            class="ea-copy-btn btn btn--secondary btn--small"
            data-copy-value="${code}"
            aria-label="Copy referral code ${code}"
            type="button"
          >Copy</button>
        </div>
        <p class="ea-tier-block__referral-note">
          Share your code. Your friend gets <strong>${friendPct}% off</strong>.
          You earn <strong>${creditPct}% credit</strong> when they order.
        </p>
      </div>
    `;
  }

  function handleCopyReferralCode(e) {
    const btn = e.currentTarget;
    const code = btn.dataset.copyValue;
    if (!code) return;

    navigator.clipboard.writeText(code).then(function () {
      const original = btn.textContent;
      btn.textContent = 'Copied!';
      btn.setAttribute('aria-label', 'Code copied to clipboard');
      setTimeout(function () {
        btn.textContent = original;
        btn.setAttribute('aria-label', `Copy referral code ${code}`);
      }, 2000);
    }).catch(function () {
      // Fallback for older browsers
      const el = document.getElementById('ea-ref-code');
      if (el) {
        const range = document.createRange();
        range.selectNode(el);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        document.execCommand('copy');
        window.getSelection().removeAllRanges();
      }
    });
  }

  // ─── EA PRICING ──────────────────────────────────────────────────────────

  /**
   * Reads tier from session and reveals the correct sale price on every
   * .ea-price element. Prices were pre-computed server-side in Liquid
   * and embedded as data-* attributes — no currency maths in JS.
   *
   * @param {Object} session
   */
  function revealEAPricing(session) {
    const tier = (session.tier || 'INSIDER').toLowerCase(); // 'expert' | 'essentialist' | 'insider'

    document.querySelectorAll('.ea-price').forEach(function (el) {
      const wasPrice = el.dataset.eaWas;
      const nowPriceKey = 'ea' + tier.charAt(0).toUpperCase() + tier.slice(1);
      const nowPrice = el.dataset[nowPriceKey];
      const pctKey = 'eaPct' + tier.charAt(0).toUpperCase() + tier.slice(1);
      const pct = el.dataset[pctKey];

      if (!nowPrice || !wasPrice) return;

      const originalEl = el.querySelector('.ea-price__original');
      if (!originalEl) return;

      // Strike-through the original/was price
      originalEl.outerHTML = `
        <s class="ea-price__was f-price-item f-price-item--regular" aria-label="Was ${wasPrice}">
          ${wasPrice}
        </s>
      `;

      // Inject sale price
      const nowEl = document.createElement('span');
      nowEl.className = 'ea-price__now f-price-item f-price-item--sale';
      nowEl.setAttribute('aria-label', `Now ${nowPrice}`);
      nowEl.textContent = nowPrice;

      // Inject save badge
      const badgeEl = document.createElement('span');
      badgeEl.className = 'f-badge f-badge--sale ea-price__badge small-bold';
      badgeEl.setAttribute('aria-label', `Save ${pct}%`);
      badgeEl.textContent = `Save ${pct}%`;

      el.appendChild(nowEl);
      el.appendChild(badgeEl);

      el.classList.add('f-price--on-sale', 'ea-price--resolved');
    });
  }

  // ─── DISCOUNT APPLICATION ─────────────────────────────────────────────────

  /**
   * Applies the customer's discount code via the /discount/CODE Shopify endpoint.
   * This sets a server-side cookie that persists through the Ajax cart
   * and carries through to checkout — compatible with this theme's cart system.
   *
   * Only fires ONCE per session (guarded by sessionStorage).
   * Prevents infinite redirect loops.
   *
   * @param {Object} session
   */
  function applyDiscountCode(session) {
    const code = session.discount_code;
    if (!code) return;

    // Check if already applied in this browser session
    if (sessionStorage.getItem(DISCOUNT_APPLIED_KEY) === code) {
      return;
    }

    // Check if we just returned from the discount redirect
    // (URL will have ?ea_discount_applied=1 appended by the redirect)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('ea_discount_applied') === '1') {
      // Mark as applied and clean up URL
      sessionStorage.setItem(DISCOUNT_APPLIED_KEY, code);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      return;
    }

    // Fire the discount redirect with return URL including our flag
    const returnUrl = encodeURIComponent(PAGE_HANDLE + '?ea_discount_applied=1');
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${returnUrl}`;
  }

  // ─── REORDER BLOCK ────────────────────────────────────────────────────────

  /**
   * Renders the previous orders reorder block.
   * Shows up to 3 most recent order line items with
   * their original price vs EA price + reorder button.
   *
   * @param {Object} session
   */
  function renderReorderBlock(session) {
    const section = document.getElementById('ea-reorder');
    const grid = document.getElementById('ea-reorder-grid');
    if (!section || !grid) return;

    const orders = session.previous_orders.slice(0, 3);
    const tier = (session.tier || 'INSIDER').toLowerCase();
    const discountPct = session.discount_pct || 20;
    const currency = session.currency || 'GBP';

    const cards = orders.map(function (order) {
      return buildReorderCard(order, tier, discountPct, currency);
    }).join('');

    grid.innerHTML = cards;
    section.style.display = '';

    // Bind reorder buttons
    grid.querySelectorAll('.ea-reorder-btn').forEach(function (btn) {
      btn.addEventListener('click', handleReorder);
    });
  }

  function buildReorderCard(order, tier, discountPct, currency) {
    const symbol = currency === 'GBP' ? '£' : '$';
    const items = (order.line_items || []).slice(0, 4);

    const itemsHtml = items.map(function (item) {
      const originalPrice = (item.price / 100).toFixed(2);
      const salePrice = ((item.price * (100 - discountPct)) / 10000).toFixed(2);
      const imageHtml = item.image
        ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" width="60" height="60" loading="lazy">`
        : `<div class="ea-reorder-card__img-placeholder"></div>`;

      return `
        <div class="ea-reorder-card__item">
          ${imageHtml}
          <div class="ea-reorder-card__item-info">
            <p class="ea-reorder-card__item-title">${escapeHtml(item.title)}</p>
            <p class="ea-reorder-card__item-price">
              <s>${symbol}${originalPrice}</s>
              <strong>${symbol}${salePrice}</strong>
            </p>
          </div>
        </div>
      `;
    }).join('');

    const variantIds = items
      .filter(function (item) { return item.variant_id; })
      .map(function (item) { return item.variant_id; })
      .join(',');

    return `
      <div class="ea-reorder-card">
        <p class="ea-reorder-card__date">Order from ${formatDate(order.created_at)}</p>
        <div class="ea-reorder-card__items">${itemsHtml}</div>
        <button
          class="ea-reorder-btn btn btn--primary"
          data-variant-ids="${variantIds}"
          data-order-id="${order.id}"
          type="button"
          aria-label="Reorder items from ${formatDate(order.created_at)} at ${discountPct}% off"
        >
          Reorder at ${discountPct}% off — add all to bag
        </button>
      </div>
    `;
  }

  function handleReorder(e) {
    const btn = e.currentTarget;
    const variantIds = (btn.dataset.variantIds || '').split(',').filter(Boolean);
    if (variantIds.length === 0) return;

    btn.textContent = 'Adding…';
    btn.disabled = true;

    // Use theme's FoxThemeCartHelpers if available, otherwise fall back to Ajax API
    const addItems = variantIds.map(function (id) {
      return { id: parseInt(id, 10), quantity: 1 };
    });

    addToCart(addItems).then(function () {
      btn.textContent = 'Added to bag!';
      setTimeout(function () {
        btn.textContent = 'Reorder at ' + (btn.getAttribute('aria-label').match(/(\d+)%/) || ['', ''])[1] + '% off — add all to bag';
        btn.disabled = false;
      }, 2500);
    }).catch(function (err) {
      console.error('[EA] Reorder failed:', err);
      btn.textContent = 'Something went wrong — please try again';
      btn.disabled = false;
    });
  }

  /**
   * Adds items to cart using the theme's Ajax Cart API.
   * Falls back to a direct Shopify /cart/add.js fetch.
   *
   * @param {Array<{id: number, quantity: number}>} items
   * @returns {Promise}
   */
  function addToCart(items) {
    // Use the theme's helper if available (ensures cart drawer opens correctly)
    if (window.FoxThemeCartHelpers && typeof window.FoxThemeCartHelpers.addItems === 'function') {
      return window.FoxThemeCartHelpers.addItems(items);
    }

    // Fallback: direct Ajax Cart API
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);
      return res.json();
    });
  }

  // ─── GWP PROGRESS BAR ────────────────────────────────────────────────────

  /**
   * Initialises the Ritual Case (GWP) progress bar.
   * Subscribes to cart updates via FoxThemeEvents pub/sub so the bar
   * updates in real-time as items are added/removed from cart drawer.
   *
   * IMPORTANT: cart.total_price from Ajax API is pre-discount.
   * We apply the tier discount percentage client-side to get the true
   * amount the customer will pay, then compare against the GWP threshold.
   *
   * @param {Object} session
   */
  function initGWPBar(session) {
    const bar = document.getElementById('ea-gwp-bar');
    if (!bar) return;

    const currency = (session.currency || 'GBP').toUpperCase();
    const threshold = currency === 'GBP'
      ? (session.ritual_case_threshold_gbp || 90) * 100   // in pence
      : (session.ritual_case_threshold_usd || 125) * 100; // in cents
    const discountPct = session.discount_pct || 20;
    const symbol = currency === 'GBP' ? '£' : '$';

    function updateBar(cartTotalRaw) {
      // Apply tier discount to get true spend amount
      const discountedTotal = Math.round(cartTotalRaw * (100 - discountPct) / 100);
      const remaining = Math.max(0, threshold - discountedTotal);
      const progress = Math.min(100, Math.round((discountedTotal / threshold) * 100));

      const progressEl = bar.querySelector('.ea-gwp-bar__fill');
      const textEl = bar.querySelector('.ea-gwp-bar__text');

      if (progressEl) progressEl.style.width = progress + '%';

      if (textEl) {
        if (remaining === 0) {
          textEl.textContent = 'Your free Ritual Case has been added!';
          bar.classList.add('ea-gwp-bar--qualified');
        } else {
          const remainingFormatted = (remaining / 100).toFixed(2);
          textEl.textContent = `Add ${symbol}${remainingFormatted} more for your free Ritual Case`;
          bar.classList.remove('ea-gwp-bar--qualified');
        }
      }
    }

    // Initial state: fetch current cart
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) { updateBar(cart.total_price); })
      .catch(function () { /* GWP bar is non-critical — fail silently */ });

    // Subscribe to cart updates (fired by the theme's cart drawer on every add/remove)
    if (window.FoxThemeEvents && window.FoxThemeEvents.subscribe) {
      window.FoxThemeEvents.subscribe('cart:updated', function (cartData) {
        if (cartData && cartData.total_price !== undefined) {
          updateBar(cartData.total_price);
        } else {
          // Re-fetch if payload doesn't include total_price
          fetch('/cart.js')
            .then(function (res) { return res.json(); })
            .then(function (cart) { updateBar(cart.total_price); })
            .catch(function () {});
        }
      });
    }
  }

  // ─── STATE HELPERS ────────────────────────────────────────────────────────

  function setSkeletonState(active) {
    const tierBlock = document.getElementById('ea-tier-block');
    if (tierBlock) {
      if (active) {
        tierBlock.classList.add('ea-skeleton');
        tierBlock.setAttribute('aria-busy', 'true');
      } else {
        tierBlock.classList.remove('ea-skeleton');
      }
    }

    document.querySelectorAll('.ea-price').forEach(function (el) {
      el.classList.toggle('ea-price--loading', active);
    });
  }

  function setGateStatus(message) {
    const el = document.getElementById('ea-gate-status');
    if (el) el.textContent = message;
  }

  function setPreLaunchState(firstName) {
    setGateStatus('Early Access not yet open.');
    const tierBlock = document.getElementById('ea-tier-block');
    if (tierBlock) {
      tierBlock.innerHTML = `
        <div class="ea-tier-block__prelaunch">
          <p>Hi ${escapeHtml(firstName || 'there')}, your Early Access opens on 30 May 2026 at 8:00 AM UTC.</p>
          <p>Check back then to unlock your exclusive discount.</p>
        </div>
      `;
      tierBlock.classList.add('ea-tier-block--resolved');
    }
  }

  function setClosedState() {
    setGateStatus('Early Access has closed.');
    const tierBlock = document.getElementById('ea-tier-block');
    if (tierBlock) {
      tierBlock.innerHTML = `
        <div class="ea-tier-block__closed">
          <p>The Mid-Year Ritual Reset Early Access closed on 21 June 2026.</p>
          <p>Thank you for being part of the Sachi community.</p>
        </div>
      `;
      tierBlock.classList.add('ea-tier-block--resolved');
    }
  }

  function setErrorState(message) {
    setSkeletonState(false);
    setGateStatus(message);
    const tierBlock = document.getElementById('ea-tier-block');
    if (tierBlock) {
      tierBlock.innerHTML = `
        <p class="ea-tier-block__error" role="alert">${escapeHtml(message)}</p>
      `;
      tierBlock.classList.add('ea-tier-block--resolved');
    }
  }

  // ─── SESSION CACHE ────────────────────────────────────────────────────────

  function getCachedSession() {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      // Basic TTL check (Supabase sets expires_at — use it if present)
      if (cached.expires_at && new Date(cached.expires_at) < new Date()) {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      }
      return cached;
    } catch (e) {
      return null;
    }
  }

  function setCachedSession(session) {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch (e) {
      // sessionStorage quota exceeded — not critical
    }
  }

  // ─── UTILITIES ────────────────────────────────────────────────────────────

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (e) {
      return iso;
    }
  }

}());
